<template>

    <div class="attachment-manager">

        <file-upload
            class="btn btn-primary btn-icon btn-ghost-blue float-right mb-3"
            input-id=""
            :post-action="postAction"
            extensions="pdf"
            accept="accept"
            :headers="{Authorization: $store.state.currentUser.auth_key}"
            :multiple="false"
            :size="1024 * 1024 * 10"
            v-model="files"
            @input-file="inputFile"
            ref="upload">
            <b-icon icon="upload" class="mr-2"></b-icon>
            Upload Document
        </file-upload>

        <div class="clearfix"></div>

        <div v-for="group in attachmentGroupKeys" :key="group">
            <h3 class="mt-4 mb-2">
                {{ group ? group : 'Uncategorized' }}
            </h3>

            <b-card class="my-1"
                    v-for="(attachment, index) in attachmentGroups[group]" :key="index">
                <b-row cols="$mq == 'sm' ? 4 : 2">
                    <b-col>
                        <span class="mr-3">
                            {{ attachment.name }}
                        </span>
                        <b-badge variant="danger"
                                 v-if="!attachment.file_exists">
                            {{ $i18n.gt('BADGE_FILE_NOT_FOUND') }}
                        </b-badge>
                    </b-col>
                    <b-col class="text-right">
                        <b-btn size="sm" variant="primary" class="btn-ghost-blue mr-2 mb-2"
                               @click="download(attachment, false)">
                            <b-icon icon="download"></b-icon>
                        </b-btn>

                        <b-dropdown right size="sm" variant="primary" toggle-class="btn-ghost-blue mr-2 mb-2">
                            <template #button-content>
                                <b-icon icon="diagram3"></b-icon>
                            </template>

                            <b-dropdown-header>
                                <label>Select Category</label>
                            </b-dropdown-header>
                            <template v-for="group2 in attachmentGroupKeys">
                                <b-dropdown-item-btn :key="group2"
                                                     @click="changeGroup(attachment, group2)">
                                    <div :class="{'attachment-manager__current-category': group2 == group}">
                                        {{ group2 ? group2 : 'Uncategorized' }}
                                    </div>
                                </b-dropdown-item-btn>
                            </template>

                            <b-dropdown-divider></b-dropdown-divider>

                            <b-dropdown-header>
                                <label>New Category</label>
                            </b-dropdown-header>
                            <b-dropdown-form>
                                <b-form-input type="text" class="attachment-manager__new-category-input"
                                              debounce="500"
                                              v-model="attachment.new_group">
                                </b-form-input>
                            </b-dropdown-form>
                            <b-dropdown-item-btn class="text-right">
                                Cancel
                            </b-dropdown-item-btn>
                            <b-dropdown-item-btn class="text-right" variant="primary"
                                                 @click="updateGroup(attachment)">
                                Save
                            </b-dropdown-item-btn>

                        </b-dropdown>

                        <b-btn size="sm" variant="danger" class="mr-2 mb-2"
                               @click="remove(attachment)">
                            <b-icon icon="trash"></b-icon>
                        </b-btn>
                    </b-col>
                </b-row>
            </b-card>
        </div>

    </div>

</template>

<script>

/**
 * Component to list attachments for a model, by group, and allow uploading or removing attachments
 * or moving them between groups. This differs from AttachmentList in that the latter only displays
 * attachments for a single group.
 */

import FileUpload from 'vue-upload-component'
import * as _ from 'lodash'
import Attachments from "@/providers/Attachments"

export default {
    name: 'AttachmentManager',
    components: {
        FileUpload
    },
    props: {
        // NB: we could probably compute one of modelClass/modelTable from the other,
        // but it's complicated, so let's just be explicit...
        modelClass: String, // unqualified PHP class name of model (used in g_attachment table)
        modelTable: String, // PHP controller path for model (used in api requests)
        modelId: Number,
        extensions: String, // allowable file extensions - e.g. "pdf, jpg"
        accept: String      // accepted content types - e.g. "application/pdf, image/jpeg"
    },
    data() {
        return {
            modelInstance: Object,
            files: []
        }
    },
    computed: {
        attachmentGroups() {
            return Attachments.getGroupedSortedAttachments(this.modelInstance.attachments)
        },
        attachmentGroupKeys() {
            return _.sortBy(_.keys(this.attachmentGroups))
        },
        postAction() {
            return this.$api.getAttachmentUploadUrl(this.modelClass, this.modelId, '')
        }
    },
    methods: {
        // called when a different group is selected from the dropdown
        changeGroup(attachment, group) {
            attachment.group = group
            this.saveGroup(attachment)
        },
        // called when a new group name is entered in the dropdown
        updateGroup(attachment) {
            // NB: new_group is an ephemeral attribute, for convenient binding to the input
            if (attachment.new_group) {
                attachment.group = attachment.new_group
            }
            attachment.new_group = ''
            this.saveGroup(attachment)
        },
        async saveGroup(attachment) {
            await this.$api.updateItem(this, 'attachment', attachment)
            this.$toasted.show('Category Updated', {duration: 3000})
        },
        download(attachment, preview) {
            this.$api.downloadAttachment(attachment, preview)
                .catch((e) => {
                    this.$utils.handleError(e, 'Could not download file')
                })
        },
        async remove(attachment) {
            if (await this.$bvModal.msgBoxConfirm('Delete attached file?')) {
                try {
                    await this.$api.deleteItem(this, 'attachment', attachment)
                    this.refresh()
                } catch (e) {
                    this.$utils.handleError(e, 'Could not remove file')
                }
            }
        },
        inputFile(newFile, oldFile) {
            // when a file is selected, auto-start upload
            if (newFile && !oldFile) {
                this.$refs.upload.active = true
            }
        },
        async refresh() {
            // reload the parent model and its attachment list
            const item = await this.$api.getItem(this, this.modelTable, this.modelId)
            this.modelInstance = item
        }
    },
    mounted() {
        this.refresh()

        // refresh after a file is uploaded
        this.$watch(
            () => {
                return this.$refs.upload.uploaded
            },
            (val) => {
                if (val) {
                    this.refresh()
                }
            }
        )
    },

}
</script>

<style lang="scss">

.attachment-manager__current-category {
    font-weight: bold;
}

.attachment-manager__new-category-input {
    min-width: 200px;
}

</style>
